import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Industry {
  id: string;
  name: string;
  icon: string;
  description: string;
  image: string;
  alt: string;
  keyBenefits: string[];
}

const IndustryShowcase = () => {
  const industries: Industry[] = [
  {
    id: 'healthcare',
    name: 'Healthcare',
    icon: 'HeartIcon',
    description: 'HIPAA-compliant IT solutions for medical practices and healthcare facilities',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14c968347-1764642437513.png",
    alt: 'Modern hospital corridor with medical equipment and healthcare professionals using digital tablets',
    keyBenefits: ['HIPAA Compliance', 'EHR Integration', 'Secure Data Storage']
  },
  {
    id: 'finance',
    name: 'Financial Services',
    icon: 'CurrencyDollarIcon',
    description: 'Secure, compliant technology infrastructure for financial institutions',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fee79760-1766555481576.png",
    alt: 'Professional financial office with traders monitoring multiple screens showing market data',
    keyBenefits: ['SOC 2 Compliance', 'Fraud Prevention', 'Real-time Monitoring']
  },
  {
    id: 'manufacturing',
    name: 'Manufacturing',
    icon: 'CogIcon',
    description: 'Industrial IoT and automation solutions for modern manufacturing',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_137c3a868-1769200761642.png",
    alt: 'Advanced manufacturing facility with robotic arms and automated assembly lines',
    keyBenefits: ['IoT Integration', 'Process Automation', 'Supply Chain Optimization']
  },
  {
    id: 'retail',
    name: 'Retail',
    icon: 'ShoppingCartIcon',
    description: 'Omnichannel retail technology and POS system management',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18ee94cf0-1768855842453.png",
    alt: 'Modern retail store interior with digital displays and point of sale systems',
    keyBenefits: ['POS Systems', 'Inventory Management', 'Customer Analytics']
  },
  {
    id: 'education',
    name: 'Education',
    icon: 'AcademicCapIcon',
    description: 'Learning management systems and campus-wide IT infrastructure',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_156fda0c3-1767031696783.png",
    alt: 'University classroom with students using laptops and interactive digital whiteboard',
    keyBenefits: ['LMS Integration', 'Campus Wi-Fi', 'Student Data Security']
  },
  {
    id: 'small-business',
    name: 'Small Business',
    icon: 'BuildingStorefrontIcon',
    description: 'Scalable IT solutions designed for growing small businesses',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bfed83ce-1767450544825.png",
    alt: 'Small business office with entrepreneurs collaborating around modern workstations',
    keyBenefits: ['Cost-Effective Solutions', 'Scalable Infrastructure', 'Business Growth Support']
  }];


  return (
    <section className="py-20 bg-card">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="font-headline font-bold text-3xl lg:text-4xl text-primary mb-4">
            Industry-Specific Expertise
          </h2>
          <p className="text-lg text-muted-foreground font-body max-w-2xl mx-auto">
            Tailored IT solutions that understand your industry&apos;s unique challenges and compliance requirements
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {industries.map((industry) =>
          <div
            key={industry.id}
            className="group bg-muted rounded-xl overflow-hidden shadow-card hover:shadow-elevated transition-all duration-300 hover:scale-105">

              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <AppImage
                src={industry.image}
                alt={industry.alt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />

                <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent" />
                <div className="absolute bottom-4 left-4 flex items-center space-x-3">
                  <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center">
                    <Icon name={industry.icon as any} size={24} variant="outline" className="text-white" />
                  </div>
                  <h3 className="font-headline font-bold text-2xl text-white">
                    {industry.name}
                  </h3>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <p className="text-foreground font-body mb-4">
                  {industry.description}
                </p>

                {/* Key Benefits */}
                <div className="space-y-2 mb-6">
                  {industry.keyBenefits.map((benefit, index) =>
                <div key={index} className="flex items-center space-x-2">
                      <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-success" />
                      <span className="text-sm text-muted-foreground font-body">{benefit}</span>
                    </div>
                )}
                </div>

                {/* CTA */}
                <Link
                href="/industry-solutions"
                className="flex items-center justify-center space-x-2 w-full px-4 py-3 bg-accent hover:bg-accent/90 text-white rounded-lg font-cta font-semibold text-sm transition-colors duration-200">

                  <span>Explore Solutions</span>
                  <Icon name="ArrowRightIcon" size={16} variant="outline" />
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <Link
            href="/industry-solutions"
            className="inline-flex items-center space-x-2 px-8 py-4 bg-primary hover:bg-secondary text-white rounded-lg font-cta font-bold text-lg shadow-card hover:shadow-elevated transition-all duration-300">

            <span>View All Industry Solutions</span>
            <Icon name="ArrowRightIcon" size={20} variant="outline" />
          </Link>
        </div>
      </div>
    </section>);

};

export default IndustryShowcase;